import { useQuery } from "@tanstack/react-query"
import { getRouteApi } from "@tanstack/react-router"
import { Download } from "lucide-react"

import { Button } from "@atlant/ui"

import { graphql } from "#gql"
import type { InvoiceMonthInboxQuery } from "#gql/graphql.ts"
import { queryKeys } from "#lib/query-keys.ts"
import { FleetListDocument } from "#pages/authenticated/fleet/page.tsx"
import { useLoggedInUserInfo } from "#store/user-info.ts"
import { DateConverter } from "#utils/date.ts"
import { client } from "#utils/graphql-client.ts"

import { InvoiceTabs } from "../components/invoice-tabs.tsx"
import { StatusBadge } from "../components/status-badge.tsx"
import { InvoiceMonthSummariesDocument } from "../page.tsx"
import { InvoiceDetail } from "./components/invoice-detail.tsx"
import { InvoiceInbox } from "./components/invoice-inbox.tsx"
import { MonthKpis } from "./components/month-kpis.tsx"

export const InvoiceMonthInboxDocument = graphql(/* GraphQL */ `
  query InvoiceMonthInbox($input: QueryInvoiceMonthInboxInput!) {
    invoiceMonthInbox(input: $input) {
      categoryId
      code
      description
      invoiceCount
      lateCount
      totals {
        currency
        amount
      }
      invoices {
        id
        aircraftId
        number
        vendor
        type
        needsReview
        source
        currency
        subtotal
        vat
        total
        airportCode
        legCode
        description
        notes
        serviceDate {
          displaydate
        }
        invoiceDate {
          displaydate
        }
        dueDate {
          displaydate
        }
        paymentDate {
          displaydate
        }
        files {
          id
          fileName
          mimeType
          blobName
          documentKind
        }
      }
    }
  }
`)

export type CategoryGroup = InvoiceMonthInboxQuery["invoiceMonthInbox"][number]
export type InboxInvoice = CategoryGroup["invoices"][number]

const route = getRouteApi("/_authenticated/$slug/invoices/$period")

type MonthSearch = ReturnType<typeof route.useSearch>

export const InvoiceMonth = () => {
  const { me } = useLoggedInUserInfo()
  const { period } = route.useParams()
  const { aircraftId, tab, invoiceId } = route.useSearch()
  const navigate = route.useNavigate()
  const organizationId = me.organization.id

  // The loader rejects invalid periods, so this always parses
  const { year, month } = DateConverter.parsePeriod(period)!
  const lateOnly = tab === "late"

  const { data: summariesData, isLoading: summariesLoading } = useQuery({
    queryKey: queryKeys.invoices.monthSummaries(organizationId, aircraftId, year),
    queryFn: () =>
      client.request(InvoiceMonthSummariesDocument, {
        input: { organizationId, aircraftId, year },
      }),
  })
  const summary = summariesData?.invoiceMonthSummaries.find((s) => s.month === month)

  const { data: inboxData, isLoading: inboxLoading } = useQuery({
    queryKey: queryKeys.invoices.monthInbox(
      organizationId,
      aircraftId,
      year,
      month,
      lateOnly,
    ),
    queryFn: () =>
      client.request(InvoiceMonthInboxDocument, {
        input: { organizationId, aircraftId, year, month, lateOnly },
      }),
    enabled: tab !== "anomaly",
  })
  const groups = inboxData?.invoiceMonthInbox ?? []

  const { data: fleetData } = useQuery({
    queryKey: queryKeys.aircraft.list(organizationId),
    queryFn: () =>
      client.request(FleetListDocument, {
        input: { organizationId, sources: ["LEON", "MANUAL"] },
      }),
  })
  const aircraftLabel = aircraftId
    ? (fleetData?.aircraftByOrganization.find((ac) => ac.id === aircraftId)?.tail ?? "")
    : "All aircraft"

  const selected = groups
    .flatMap((group) => group.invoices.map((invoice) => ({ invoice, group })))
    .find(({ invoice }) => invoice.id === invoiceId)

  const updateSearch = (patch: Partial<MonthSearch>) =>
    // Router is not type-registered, so the reducer param needs an explicit type
    navigate({
      to: ".",
      search: (prev: MonthSearch) => ({ ...prev, ...patch }),
      replace: true,
    })

  const title = DateConverter.formatPeriod(year, month)

  return (
    <div className="flex h-full w-full flex-col gap-4 overflow-auto p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold">{title}</h1>
            {/* Month workflow (In review / Send / Closed) is not modelled yet */}
            <StatusBadge status={summary ? "new" : "notStarted"} />
          </div>
          <p className="text-muted-foreground text-sm">{aircraftLabel}</p>
        </div>
        {/* Download all is not ticketed yet */}
        <Button disabled>
          <Download />
          Download all
        </Button>
      </div>

      <InvoiceTabs
        tabs={[
          { value: "all", label: "All" },
          { value: "late", label: "Late invoices", badge: summary?.lateCount },
          { value: "anomaly", label: "Anomaly" },
        ]}
        value={tab}
        onChange={(value) => updateSearch({ tab: value, invoiceId: undefined })}
        layoutId="invoices-month-tab-indicator"
      />

      {tab === "anomaly" ? (
        <div className="text-muted-foreground flex flex-1 items-center justify-center text-lg">
          Coming soon
        </div>
      ) : (
        <>
          <MonthKpis summary={summary} isLoading={summariesLoading} />
          {/* Desktop: panels fill the viewport and scroll inside; mobile: the page scrolls */}
          <div className="grid grid-cols-1 gap-4 lg:min-h-0 lg:flex-1 lg:grid-cols-2">
            <InvoiceInbox
              groups={groups}
              isLoading={inboxLoading}
              selectedId={invoiceId}
              onSelect={(id) => updateSearch({ invoiceId: id })}
              emptyText={
                lateOnly ? "No late invoices this month" : "No invoices for this month"
              }
            />
            <InvoiceDetail invoice={selected?.invoice} group={selected?.group} />
          </div>
        </>
      )}
    </div>
  )
}
